import { useMutation } from "@tanstack/react-query";

import { axios as axiosInstance } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";
import { useAuthStore } from "@/client/stores/auth";
import { useTokenStore } from "@/client/stores/token";
import { addLocaleToPath, getLocaleFromPath } from "@/client/utils/url-locales";

export const logout = async () => {
  try {
    const response = await axiosInstance.post("/auth/logout");
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { status?: number }; message?: string };
    console.error("[Logout] API call failed:", err.response?.status, err.message);
    // Even if there's an error (like 401 Unauthorized), we want to clear user state
    // This is normal if the session has already expired
    // Do nothing, just silently handle the error
  }
};

// Helper to get localized login path
const getLocalizedLoginPath = () => {
  // Try to get locale from current URL first, then localStorage
  const urlLocale = getLocaleFromPath(window.location.pathname);
  const storedLocale = window.localStorage.getItem("locale");
  const locale = urlLocale || storedLocale;

  return locale ? addLocaleToPath("/auth/login", locale) : "/auth/login";
};

// Aggressively clear all auth state before redirect
const clearAllAuthState = () => {
  // 1. Clear Zustand persist storage FIRST (synchronous localStorage call)
  localStorage.removeItem("auth");

  // 2. Clear token store (in-memory + persisted)
  useTokenStore.getState().reset();
  localStorage.removeItem("token-store");

  // 3. Clear Zustand in-memory state
  useAuthStore.setState({ user: null, cachedSubscriptionTier: null, subscriptionTier: "FREE" });

  // 3. Clear React Query cache
  queryClient.setQueryData(["user"], null);
  queryClient.clear();

  // 4. Clear any other auth-related storage
  sessionStorage.clear();
};

export const useLogout = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: logoutFn,
  } = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      clearAllAuthState();
      const loginPath = getLocalizedLoginPath();
      window.location.href = loginPath;
    },
    onError: (err) => {
      console.error("[Logout] onError triggered:", err);
      clearAllAuthState();
      const loginPath = getLocalizedLoginPath();
      window.location.href = loginPath;
    },
  });

  return { logout: logoutFn, loading, error };
};
